import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor, SamplingDecision } from '@opentelemetry/sdk-trace-base';
import type { ReadableSpan, Sampler, SamplingResult, SpanExporter } from '@opentelemetry/sdk-trace-base';
import { ExportResultCode, W3CTraceContextPropagator } from '@opentelemetry/core';
import type { ExportResult } from '@opentelemetry/core';
import { propagation } from '@opentelemetry/api';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_DEPLOYMENT_ENVIRONMENT } from '@opentelemetry/semantic-conventions/incubating';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import { JsonTraceSerializer } from '@opentelemetry/otlp-transformer';
import { proxyTraces } from '@ordr-as/ordr-service-types/epos';
import config from './config';
import { isAdyenDevice } from './utils/isAdyenDevice';

let currentLocationId: number | undefined;
let paymentSampled = false;

export function setTracingLocationId(id: number) {
  currentLocationId = id;
}

export function setPaymentTracingActive(active: boolean, mode: 'default' | 'direct-sale' = 'default') {
  paymentSampled = active && Math.random() < (mode === 'direct-sale' ? 0.4 : 0.1);
}

const baseUrls = [config.bff, config.ordrServiceUrl, config.paymentServiceUrl, config.menuBuilderUrl];

function normalizePath(url: string): string | null {
  const base = baseUrls.find((b) => url.startsWith(b));
  if (!base) {
    return null;
  }
  return url
    .slice(base.length)
    .replace(/[?#].*$/, '')
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/{id}')
    .replace(/\/[A-Z0-9]{10,}-\d+/g, '/{ref}')
    .replace(/\/\d+/g, '/{id}');
}

if (!['local', 'localQa', 'localProd'].includes(config.env) && !isAdyenDevice()) {
  const paymentSampler: Sampler = {
    shouldSample(): SamplingResult {
      return { decision: paymentSampled ? SamplingDecision.RECORD_AND_SAMPLED : SamplingDecision.NOT_RECORD };
    },
    toString() {
      return 'PaymentSampler';
    },
  };

  class OrdrServiceTraceExporter implements SpanExporter {
    export(spans: ReadableSpan[], resultCallback: (result: ExportResult) => void): void {
      const serialized = JsonTraceSerializer.serializeRequest(spans);
      if (!serialized) {
        resultCallback({ code: ExportResultCode.SUCCESS });
        return;
      }
      proxyTraces(JSON.parse(new TextDecoder().decode(serialized)) as string)
        .then(() => resultCallback({ code: ExportResultCode.SUCCESS }))
        .catch((error: unknown) => resultCallback({ code: ExportResultCode.FAILED, error: error as Error }));
    }

    shutdown(): Promise<void> {
      return Promise.resolve();
    }
  }

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: 'epos-frontend',
      [ATTR_DEPLOYMENT_ENVIRONMENT]: config.env,
    }),
    sampler: paymentSampler,
    spanProcessors: [new BatchSpanProcessor(new OrdrServiceTraceExporter())],
  });

  propagation.setGlobalPropagator(new W3CTraceContextPropagator());
  provider.register();

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        applyCustomAttributesOnSpan(_span, request) {
          const url = request instanceof Request ? request.url : undefined;
          if (!url) return;

          const entry = (performance.getEntriesByType('resource') as PerformanceResourceTiming[]).findLast(
            (e) => e.name === url
          );
          if (!entry) return;

          const ttfb = entry.responseStart - entry.requestStart;
          const tcp = entry.connectEnd - entry.connectStart;
          const dns = entry.domainLookupEnd - entry.domainLookupStart;

          if (ttfb > 0) _span.setAttribute('network.ttfb_ms', ttfb);
          if (tcp > 0) _span.setAttribute('network.tcp_ms', tcp);
          if (dns > 0) _span.setAttribute('network.dns_ms', dns);
        },
        ignoreUrls: [/\/oltp-proxy\//, /sentry\.io/],
        propagateTraceHeaderCorsUrls: [
          new RegExp(config.bff.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
          new RegExp(config.ordrServiceUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
          new RegExp(config.paymentServiceUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
          new RegExp(config.menuBuilderUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
        ],
        requestHook(span, request) {
          if (currentLocationId !== undefined) span.setAttribute('app.location_id', currentLocationId);

          const url = request instanceof Request ? request.url : undefined;
          const method = request instanceof Request ? request.method : (request as RequestInit).method;

          if (!url) return;

          const path = normalizePath(url);
          if (path) span.updateName(`${method ?? 'GET'} ${path}`);
        },
        semconvStabilityOptIn: 'http',
      }),
    ],
  });
}
